import { Alert, Col, Heading, Loader, NewModal as Modal, Row, TextInput, themes } from '@digitools/honest-react';
import { BannerMenu, Breadcrumbs, breakpoint, spacing } from '@digitools/honest-ui';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatName } from 'src/utils/validationUtils';
import { useGetLandingPageResource } from 'src/api/LandingPageApi';
import Notifications, { FunctionalArea } from '../../../components/Notifications/Notifications';
import { TEALIUM_EVENT, TEALIUM_PAGE_VIEW } from 'src/constants/tealiumConstants';
import useAuthentication from '../../../hooks/useAuthentication';
import useCustomerConfig from '../../../hooks/useCustomerConfig';
import useExperience from '../../../hooks/useExperience';
import useTealium from '../../../hooks/useTealium';
import useTranslate from '../../../hooks/useTranslate';
import { Experience } from 'src/types/Experience';
import { Language } from 'src/types/Language';
import { getEnvVariable } from 'src/utils/envUtils';
import CustomerBar from '../components/CustomerBar/CustomerBar';
import ContentBlocks from '../components/landingPageContent/ContentBlocks';
import LandingPageBanner, { useBannerMenuItems } from '../components/landingPageContent/LandingPageBanner';
import { a11yClick, getContentForModal, logoutMethod, renderModalFooter, renderModalFooterCancelButton, updateTermsAndConditions } from './TermsAndConditionsModalContent';
import LogRocket from 'logrocket';
import { withRouter } from 'src/utils/withRouter';
import ProvePrompt from 'src/components/prove/ProvePrompt';
import { useDisclosure } from '@mantine/hooks';
import { needsTermsAndConditions } from 'src/utils/termsAndConditionsUtils';
import QuickLinks from 'src/packages/landingPages/components/CustomerBar/QuickLinks';
import MessagesContainer from 'src/packages/landingPages/components/CustomerBar/Messages/MessagesContainer';
import useMediaQuery from 'src/hooks/useMediaQuery';
import styled from 'styled-components';
import { useHomePageBreadcrumbs } from 'src/components/Breadcrumbs';
const acceptLanguage = {
    english: 'By clicking accept, you agree to the below:',
    spanish: 'Al hacer clic en aceptar, usted acepta lo siguiente',
};
const termsConditions = {
    english: 'Privacy Statement and Web Agreement',
    spanish: 'Declaración de privacidad y el Acuerdo web'
};
const breakpointMdMaxPx = Number.parseFloat(breakpoint.lg) * 16 - 1;
const breakpointMdMaxQuery = `(max-width: ${breakpointMdMaxPx}px)`;
const ActionsContainer = styled('div') `
    display: flex;
    align-items: center;
    gap: ${spacing.xs};
    justify-content: space-between;
    margin-top: ${spacing.xs};
    margin-bottom: ${spacing.xs};
`;
const HomePageContainer = () => {
    const { language, experience, changeLanguage } = useExperience();
    const { authHeader, user, logout } = useAuthentication();
    const { navigationMenu, loadingNavMenu, currentExperience } = useCustomerConfig();
    const { trackView, trackEvent } = useTealium();
    const { t } = useTranslate();
    const [landingPageResource, setLandingPageResource] = useState([]);
    const formApiRef = useRef();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const fullContext = { user, authHeader, language, changeLanguage, formApiRef, navigate, setLoading, loading, trackEvent, logout };
    const isMobile = useMediaQuery(breakpointMdMaxQuery);
    const setFormApi = (formApi) => {
        formApiRef.current = formApi;
    };
    const { loading: loadingLandingPageResource, data: landingPageResourceData, get: triggerGetLandingPageResource, } = Object(useGetLandingPageResource(experience));
    useLayoutEffect(() => {
        document.title = `My Lincoln Portal - ${experience} Home`;
        if (user?.isClientServiceRep) {
            window.location.assign(getEnvVariable('cliUrlCsr'));
        }
    });
    useEffect(() => {
        setLandingPageResource(landingPageResourceData || []);
        open();
    }, [landingPageResourceData]);
    useEffect(() => {
        const logRocketAppId = getEnvVariable('LOGROCKET_APP_ID');
        if (logRocketAppId && user && user.customerInContext) {
            LogRocket.identify(user.loginId, {
                isLFGUser: user.isLfgUser,
                isSmallMid: user.customerInContext.smallMid,
                isSSO: user.ssoUser,
            });
        }
    }, []);
    useEffect(() => {
        if (experience !== Experience.PUBLIC) {
            triggerGetLandingPageResource();
        }
    }, [experience]);
    useEffect(() => {
        if (!loadingLandingPageResource && landingPageResourceData) {
            trackView({
                page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.HOME_PAGE,
                page_l4: experience === Experience.EMPLOYER
                    ? TEALIUM_PAGE_VIEW.PAGE_L4.HOME_PAGE.HOME_PAGE_ER
                    : TEALIUM_PAGE_VIEW.PAGE_L4.HOME_PAGE.HOME_PAGE_EE,
                page_plan_code: user?.customerId,
            });
        }
    }, [loadingLandingPageResource, landingPageResourceData]);
    const trackPageEvent = (eventName, eventAction) => {
        trackEvent({
            event_action: eventAction || TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_name: eventName,
            event_type: TEALIUM_EVENT.EVENT_TYPE.CUSTOMER_ROLLUP_BAR,
        });
    };
    const hasApplications = navigationMenu &&
        navigationMenu.some(navGroup => {
            return navGroup.id.includes('REQUESTS_AND_STATUS') || navGroup.id.includes('REPORTING');
        });
    const [, { open, close }] = useDisclosure(false);
    const needsTc = needsTermsAndConditions(user);
    const menuItems = useBannerMenuItems();
    return (
    // TODO: Clean this modal up
    <>
      <Modal data-public={true} keepMounted={true} isForm={true} closeOnClickOutside={false} withCloseButton={false} closeOnEscape={false} formProps={{
            id: 'termsAndConditions-form',
            getApi: setFormApi
        }} title={language === Language.ENGLISH ? (<Heading elemType={'h2'} color={'primary'} theme={themes.rebrand}>
              To continue, complete the following for our records:
            </Heading>) : (<Heading elemType={'h2'} color={'primary'} theme={themes.rebrand}>
              Para continuar, complete lo siguiente para nuestros registros:
            </Heading>)} centered={true} opened={needsTc} onClose={() => {
            renderModalFooterCancelButton(fullContext);
            close();
        }} cancelButtonText={language === 'en' ? 'Cancel' : 'Cancelar'} submitButtonText={language === 'en' ? 'Accept' : 'Acepto'} onSubmit={() => {
            renderModalFooter(fullContext);
            close();
        }} cancelButtonProps={{
            'data-testid': 'termsAndConditionsCancelButton',
            buttonType: 'text',
            onKeyDown: (event) => {
                if (a11yClick(event)) {
                    logoutMethod(user, logout, navigate);
                }
            },
            tabIndex: 1,
            'aria-label': 'cancel terms and conditions'
        }} submitButtonProps={{
            'data-testid': 'termsAndConditionsAcceptButton',
            buttonType: 'primary',
            onKeyDown: (event) => {
                if (a11yClick(event)) {
                    //@ts-ignore
                    updateTermsAndConditions(user, authHeader(), formApiRef, navigate, setLoading, loading, trackEvent);
                }
            },
            tabIndex: 0,
            'aria-label': 'accept terms and conditions'
        }}>
        <Loader isShown={loading} size={'small'}>
          {needsTc &&
            <div data-testid={'termsAndConditionsModal'} role={'alertdialog'}>
              {getContentForModal(fullContext)}
            </div>}
          <div className={'mt-2'}>
            <strong>{t(acceptLanguage)}</strong>
            {!user?.termsAndConditionsAccepted && !user?.isLfgUser &&
            <div data-testid="termsConditionsListed">
                <strong>{t(termsConditions)}</strong>
              </div>}
            {!user?.elecConsentAcceptedDate && !user?.isLfgUser &&
            <div data-testid='eConsentListed'>
                <div>
                  <a href="https://www.lincolnfinancial.com/public/general/legal/consentforelectronictransmissions" target="_blank"><b>eTransmissions</b></a>
                </div>
                <div>
                  <a href="https://www.lincolnfinancial.com/public/general/legal/consentforelectronicsignature" target="_blank"><b>eSignatures</b></a>
                </div>
              </div>}

            {(user?.firstName?.toLowerCase() === 'EMPTY_FIRST_NAME'.toLowerCase() ||
            user?.lastName?.toLowerCase() === 'EMPTY_LAST_NAME'.toLowerCase() ||
            user?.firstName === undefined ||
            user?.lastName === undefined) && (<>
                <div style={{ overflowX: 'hidden' }}>

                  <Row>
                    <Col className={'mt-3 col-sm-12 col-md-12'}>
                      <strong>
                        {language === Language.ENGLISH
                ? 'Provide your First and Last name'
                : 'Proporcione su nombre y apellido'}
                      </strong>
                    </Col>
                  </Row>
                  <Row>
                    <Col className={'mt-1 col-sm-12 col-md-6'}>
                      <TextInput data-private={true} id={'firstName'} field={'firstName'} initialValue='' label={language === Language.ENGLISH ? 'First name' : 'Nombre'} mask={formatName} maxLength={35} type={'text'} placeholder={language === Language.ENGLISH ? 'Enter first name' : 'Ingresar nombre'} required={true} validateOnChange={true} validateOnBlur={true}/>
                    </Col>
                    <Col className={'mt-1 col-sm-12 col-md-6'}>
                      <TextInput data-private={true} id={'lastName'} field={'lastName'} initialValue='' label={language === Language.ENGLISH ? 'Last name' : 'Apellido'} mask={formatName} maxLength={35} type={'text'} placeholder={language === Language.ENGLISH ? 'Enter last name' : 'Ingresar apellido'} required={true} validateOnChange={true} validateOnBlur={true}/>
                    </Col>
                  </Row>
                </div>
              </>)}
          </div>
        </Loader>
      </Modal>
      <div data-public={true}>
          <Loader viewportCentered={true} id='home-page-spinner' isShown={loadingLandingPageResource} data-testid={'loading-indicator'}>
            {experience === Experience.EMPLOYER &&
            !loadingNavMenu &&
            currentExperience === experience &&
            navigationMenu &&
            !hasApplications && (<Alert id='erSetUpAlert' type={'error'}>
                Please contact your administrator to get set up with proper system access.
              </Alert>)}
            <Breadcrumbs language={language}>
              {useHomePageBreadcrumbs()}
            </Breadcrumbs>
            <div className={'hui-mb-sm'}>
              <Notifications functionalArea={experience === Experience.EMPLOYER ? FunctionalArea.EMPLOYER_LANDING : FunctionalArea.EMPLOYEE_LANDING}/>
            </div>
            <ProvePrompt />
            <CustomerBar landingPageResource={landingPageResource}/>
            <LandingPageBanner showMenu={!isMobile}/>
            {isMobile && (<>
                <ActionsContainer>
                  <QuickLinks trackPageEvent={trackPageEvent}/>
                  <MessagesContainer trackPageEvent={trackPageEvent} landingPageResource={landingPageResource}/>
                </ActionsContainer>
                <BannerMenu items={menuItems}/>
              </>)}
            <ContentBlocks />
          </Loader>
      </div>
    </>);
};
export default withRouter(HomePageContainer);
